import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Phone, Mail, X, Maximize2 } from 'lucide-react';
import { fetchCMSItemBySlug, fetchCMSContent } from '../lib/cms';

const serviceSeo = {
  rengoering: {
    title: "Professionel Bilrengøring i Billund | MJ Detailing",
    description: "Få professionel indvendig og udvendig rengøring af din bil i Billund hos MJ Detailing. Vi sikrer uovertruffen kvalitet og en bil, der føles som ny. Book nu!",
    image: "https://assets.auxodev.dk/assets/f1317603-54a7-41be-bffe-0256114ce69f/8b121904-21cb-408c-912b-6f90199ebcfc.jpg"
  },
  polering: {
    title: "Professionel Bilpolering i Billund | MJ Detailing",
    description: "Giv din bils lak nyt liv og en spejlblank finish med professionel bilpolering i Billund hos MJ Detailing. Vi fjerner ridser og genskaber glansen. Få tilbud!",
    image: "https://assets.auxodev.dk/assets/f1317603-54a7-41be-bffe-0256114ce69f/a98d64fb-d0fa-4c73-99a6-0de0eae8fce8.jpg"
  },
  coating: {
    title: "Keramisk Coating i Billund | MJ Detailing",
    description: "Få den ultimative lakbeskyttelse med keramisk coating i Billund hos MJ Detailing. Ekstrem glans, hydrofobisk effekt og langvarig holdbarhed. Bestil tid!",
    image: "https://assets.auxodev.dk/assets/f1317603-54a7-41be-bffe-0256114ce69f/9bfd0ae6-cc2d-467a-9a72-c9655f79966a.jpg"
  }
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const item = await fetchCMSItemBySlug('da', slug);
      setService(item);
      
      const galleryData = await fetchCMSContent('gallery');
      // Filter gallery by category if it matches service name
      if (item) {
        const filtered = galleryData.filter(g => 
          g.category.toLowerCase() === item.name.da.toLowerCase()
        );
        setGallery(filtered);
      }
      setLoading(false);
    };
    loadData();
  }, [slug]);

  const currentSeo = serviceSeo[slug] || {
    title: service ? `${service.name.da} i Billund | Professionel Bilpleje - MJ Detailing` : "Professionel Bilpleje i Billund | MJ Detailing",
    description: service ? `Få professionel ${service.name.da.toLowerCase()} i Billund hos MJ Detailing. Vi sikrer uovertruffen kvalitet, dyb glans og lang beskyttelse. Book din tid nu!` : "Professionel bilpleje, polering og keramisk coating i Billund hos MJ Detailing. Premium kvalitet og 100% tilfredshedsgaranti.",
    image: service?.image?.[0] || "https://assets.auxodev.dk/assets/f1317603-54a7-41be-bffe-0256114ce69f/9bfd0ae6-cc2d-467a-9a72-c9655f79966a.jpg"
  };

  const canonicalUrl = `https://mjdetailing.dk/ydelser/${slug}`;

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{currentSeo.title}</title>
          <meta name="description" content={currentSeo.description} />
          <meta property="og:title" content={currentSeo.title} />
          <meta property="og:description" content={currentSeo.description} />
          <meta property="og:image" content={currentSeo.image} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={currentSeo.title} />
          <meta name="twitter:description" content={currentSeo.description} />
          <meta name="twitter:image" content={currentSeo.image} />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black">
          <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </>
    );
  }

  if (!service) {
    return (
      <>
        <Helmet>
          <title>Ydelse Ikke Fundet | MJ Detailing</title>
          <meta name="description" content="Den søgte ydelse kunne desværre ikke findes hos MJ Detailing i Billund." />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <div className="min-h-screen flex flex-col items-center justify-center bg-black text-center px-4">
          <h1 className="text-4xl font-black mb-4 tracking-normal">YDELSE IKKE FUNDET</h1>
          <Link to="/" className="text-zinc-500 hover:text-white transition-colors flex items-center gap-2">
            <ArrowLeft size={20} /> Tilbage til forsiden
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <meta property="og:title" content={currentSeo.title} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:image" content={currentSeo.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentSeo.title} />
        <meta name="twitter:description" content={currentSeo.description} />
        <meta name="twitter:image" content={currentSeo.image} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 md:p-10 backdrop-blur-sm"
          >
            <motion.button
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute top-6 right-6 text-white/70 hover:text-white p-2 bg-white/10 rounded-full transition-colors z-50"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </motion.button>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-6xl w-full max-h-[85vh] flex flex-col items-center"
            >
              <img
                src={selectedImage.image?.[0]}
                alt={selectedImage.title?.da}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
              <div className="mt-6 text-center">
                <h3 className="text-xl font-bold text-white uppercase tracking-widest">{selectedImage.title?.da}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.image?.[0]}
            alt={service.name.da}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6 uppercase text-xs font-bold tracking-widest">
              <ArrowLeft size={16} /> Tilbage
            </Link>
            <h1 className="text-4xl md:text-6xl font-black tracking-normal uppercase bg-linear-to-r from-zinc-500 via-white to-zinc-500 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine">{service.name.da}</h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2 space-y-12">
              <div className="prose prose-invert prose-zinc max-w-none prose-p:text-zinc-300 prose-p:leading-relaxed prose-li:text-zinc-300 prose-strong:text-white prose-ul:list-none prose-ul:pl-0">
                <div 
                  className="space-y-8"
                  dangerouslySetInnerHTML={{ 
                    __html: service.description.da
                      .replace(/<ul>/g, '<ul class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 mt-16 pt-16 border-t border-white/10">')
                      .replace(/<li>/g, `
                        <li class="flex items-start gap-4 group">
                          <div class="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-white group-hover:border-white transition-all duration-300">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="text-white group-hover:text-black transition-colors"><path d="M20 6 9 17l-5-5"/></svg>
                          </div>
                          <span class="text-zinc-300 group-hover:text-white transition-colors duration-300">
                      `)
                      .replace(/<\/li>/g, '</span></li>')
                  }}
                />
              </div>

              {gallery.length > 0 && (
                <div className="pt-20 border-t border-white/10">
                  <h2 className="text-3xl font-black tracking-normal mb-12 uppercase bg-linear-to-r from-zinc-500 via-white to-zinc-500 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine inline-block">
                    Galleri: {service.name.da}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {gallery.map((item, index) => (
                      <motion.div 
                        key={index} 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="group relative aspect-video rounded-3xl overflow-hidden border border-white/10 bg-zinc-900 cursor-pointer"
                        onClick={() => setSelectedImage(item)}
                      >
                        <img 
                          src={item.image?.[0]} 
                          alt={item.title?.da} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-between p-8">
                          <p className="text-white font-bold tracking-wide uppercase text-sm transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                            {item.title?.da}
                          </p>
                          <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-75">
                            <Maximize2 size={20} />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar CTA */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 bg-zinc-900 border border-white/10 rounded-3xl p-8 space-y-8">
                <h3 className="text-2xl font-black tracking-normal uppercase">Interesseret?</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Vi skræddersyr altid vores ydelser til netop din bil og dine behov. Kontakt os for en uforpligtende snak og et tilbud.
                </p>
                <div className="space-y-4">
                  <a
                    href="tel:+4527895995"
                    className="flex items-center justify-center gap-3 bg-white text-black py-2.5 md:py-3.5 rounded-xl text-sm md:text-base font-bold uppercase tracking-widest hover:bg-zinc-200 transition-all"
                  >
                    <Phone size={18} /> Ring Nu
                  </a>
                  <a
                    href="mailto:mj.detailing.7190@gmail.com"
                    className="flex items-center justify-center gap-3 border border-white/20 py-2.5 md:py-3.5 rounded-xl text-sm md:text-base font-bold uppercase tracking-widest hover:bg-white/10 transition-all"
                  >
                    <Mail size={18} /> Send Email
                  </a>
                </div>
                <div className="pt-6 border-t border-white/5">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-4">Hvorfor vælge os?</p>
                  <ul className="space-y-3">
                    {['Premium produkter', 'Øje for detaljen', 'Garanti for glans'].map((text) => (
                      <li key={text} className="flex items-center gap-2 text-xs font-medium text-zinc-300">
                        <CheckCircle2 size={14} className="text-white" /> {text}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
